import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { RegisterSchema } from 'shared';
import type { RegisterInput } from 'shared';

import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { FormField } from '../components/ui/FormField';
import { useRegister } from '../features/auth/mutations';

export function Register() {
  const navigate = useNavigate();
  const registerMutation = useRegister();
  const form = useForm<RegisterInput>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: { email: '', name: '', password: '' },
  });

  const onSubmit = form.handleSubmit((values) => {
    registerMutation.mutate(values, {
      onSuccess: () => navigate('/dashboard'),
    });
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-4 py-8 text-text">
      <Card
        className="w-full max-w-md"
        header={
          <div>
            <h1 className="text-2xl font-semibold">Register</h1>
            <p className="mt-1 text-sm text-text-muted">
              Create the account that will keep the rules close.
            </p>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={onSubmit}>
          <FormField
            label="Name"
            autoComplete="name"
            error={form.formState.errors.name?.message}
            {...form.register('name')}
          />
          <FormField
            label="Email"
            type="email"
            autoComplete="email"
            error={form.formState.errors.email?.message}
            {...form.register('email')}
          />
          <FormField
            label="Password"
            type="password"
            autoComplete="new-password"
            error={form.formState.errors.password?.message}
            {...form.register('password')}
          />
          {registerMutation.isError ? (
            <p className="text-sm text-danger">Could not create account.</p>
          ) : null}
          <Button className="w-full" size="lg" type="submit" disabled={registerMutation.isPending}>
            {registerMutation.isPending ? 'Creating account...' : 'Register'}
          </Button>
          <p className="text-center text-sm text-text-muted">
            Already registered?{' '}
            <Link className="text-primary hover:underline" to="/login">
              Log in
            </Link>
          </p>
        </form>
      </Card>
    </main>
  );
}
